import type { LineItem, FFEDocument, Group, Company } from '../types/ffe'
import type { DocumentTotals } from '../types/calculations'

export const calculateLineItemTotal = (
  item: LineItem,
  materials: FFEDocument['materials']
): number => {
  // Calculate base item total
  const itemTotal = (item.sellPrice || 0) * (item.quantity || 0)

  // Calculate materials total
  const materialsTotal =
    item.materials?.reduce((total, mat) => {
      const material = materials.find((m) => m.id === mat.materialId)
      if (!material) return total
      return total + material.sellPricePerUnit * mat.quantity * item.quantity
    }, 0) || 0

  return itemTotal + materialsTotal
}

export const calculateDocumentTotals = (
  lineItems: LineItem[],
  groups: Group[],
  documentSettings: FFEDocument['settings'],
  settings: Company['settings'],
  materials: FFEDocument['materials']
): DocumentTotals => {
  const subtotal = lineItems.reduce((sum, item) => {
    // For items in alternative groups, only count the selected item
    if (item.groupId) {
      const group = groups.find((g) => g.id === item.groupId)
      if (group && group.isAlternatives && group.selectedItemId !== item.id) {
        return sum
      }
      return sum + calculateLineItemTotal(item, materials)
    }
    return sum + calculateLineItemTotal(item, materials)
  }, 0)

  if (documentSettings.taxInclusive) {
    const taxRate = settings.taxRate / 100
    const taxComponent = subtotal * (taxRate / (1 + taxRate))
    return {
      subtotal: subtotal - taxComponent,
      tax: taxComponent,
      total: subtotal
    }
  } else {
    const tax = subtotal * (settings.taxRate / 100)
    return {
      subtotal,
      tax,
      total: subtotal + tax
    }
  }
}
